import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  PERSISTENT_KEY_ACCESS_TOKEN,
  PERSISTENT_KEY_AUTH_USER,
  PERSISTENT_KEY_REFRESH_TOKEN,
} from 'src/common/constants/persistentStorageKeys';
import { isMockModeActive } from 'src/mocks/mockMode';

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const apiBaseUrl = import.meta.env.VITE_API_URL ?? '';
const apiKey = import.meta.env.VITE_API_KEY;

export const SECURE_API = axios.create({
  baseURL: apiBaseUrl,
  headers,
});

let isAlreadyFetchingAccessToken = false;

type TokenSubscriber = {
  onSuccess: (at: string) => void;
  onFailure: (err: unknown) => void;
};

let subscribers: TokenSubscriber[] = [];

const AUTH_REFRESH_PATH = '/users/token';

const isAuthRefreshRequest = (config?: { url?: string }) =>
  Boolean(config?.url?.includes(AUTH_REFRESH_PATH));

const resetTokenAndReattemptRequest = async (error: AxiosError) => {
  const errorResponse = error.response;
  if (!errorResponse?.config) {
    return Promise.reject(error);
  }

  const retryOriginalRequest = new Promise((resolve, reject) => {
    addSubscriber(
      (at: string) => {
        errorResponse.config.headers.Authorization = 'Bearer ' + at;
        resolve(axios(errorResponse.config));
      },
      reject,
    );
  });

  if (!isAlreadyFetchingAccessToken) {
    isAlreadyFetchingAccessToken = true;
    try {
      const response = await SECURE_API.post(AUTH_REFRESH_PATH, {
        'refresh-token': localStorage.getItem(PERSISTENT_KEY_REFRESH_TOKEN),
      });

      if (response.data.accessToken) {
        localStorage.setItem(PERSISTENT_KEY_ACCESS_TOKEN, response.data.accessToken);
      }
      if (response.data.refreshToken) {
        localStorage.setItem(PERSISTENT_KEY_REFRESH_TOKEN, response.data.refreshToken);
      }
      if (response.data.user) {
        localStorage.setItem(PERSISTENT_KEY_AUTH_USER, JSON.stringify(response.data.user));
        window.dispatchEvent(new CustomEvent('auth:userUpdated', { detail: response.data.user }));
      }

      const newAccessToken = response.data.accessToken as string | undefined;
      if (newAccessToken) {
        onAccessTokenFetched(newAccessToken);
      } else {
        onAccessTokenFailed(new Error('Token refresh did not return an access token'));
        handleAuthError({ force: true });
      }
    } catch (err) {
      const errorData = (err as { response?: { data?: { name?: string }; status?: number } })
        ?.response?.data;
      const status = (err as { response?: { status?: number } })?.response?.status;

      if (
        status === 401 ||
        errorData?.name === 'RefreshTokenMissingExpiredError'
      ) {
        handleAuthError({ force: true });
      }

      onAccessTokenFailed(err);
      return Promise.reject(err);
    } finally {
      isAlreadyFetchingAccessToken = false;
    }
  }

  return retryOriginalRequest;
};

function onAccessTokenFetched(at: string) {
  subscribers.forEach(({ onSuccess }) => onSuccess(at));
  subscribers = [];
}

function onAccessTokenFailed(err: unknown) {
  subscribers.forEach(({ onFailure }) => onFailure(err));
  subscribers = [];
}

function addSubscriber(onSuccess: (at: string) => void, onFailure: (err: unknown) => void) {
  subscribers.push({ onSuccess, onFailure });
}

const requestInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  if (config.headers) {
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    }

    // Read token dynamically from localStorage on each request
    const currentAccessToken = localStorage.getItem(PERSISTENT_KEY_ACCESS_TOKEN);
    if (currentAccessToken) {
      config.headers['Authorization'] = `Bearer ${currentAccessToken}`;
    }

    if (isMockModeActive()) {
      const rawUser = localStorage.getItem(PERSISTENT_KEY_AUTH_USER);
      if (rawUser) {
        try {
          const parsed = JSON.parse(rawUser) as { id?: string };
          if (parsed.id) {
            config.headers['X-Mock-User-Id'] = String(parsed.id);
          }
        } catch {
          /* ignore malformed auth payload */
        }
      }
    }
  }

  return config;
};

const responseErrorInterceptor = (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.response) {
    return Promise.reject(error);
  }

  if (error.response.status === 401) {
    const errorData = error.response.data as {
      name?: string;
      errorCode?: string;
    };

    // Do not retry refresh when the refresh endpoint itself failed
    if (isAuthRefreshRequest(error.config)) {
      handleAuthError({ force: true });
      return Promise.reject(error);
    }

    const hasRefreshToken = localStorage.getItem(PERSISTENT_KEY_REFRESH_TOKEN);

    if (hasRefreshToken) {
      return resetTokenAndReattemptRequest(error);
    }

    if (
      errorData?.name === 'RefreshTokenMissingExpiredError' ||
      errorData?.name === 'AccessTokenInvalid' ||
      errorData?.errorCode === 'missingToken'
    ) {
      handleAuthError({ force: true });
    }
  }

  return Promise.reject(error);
};

function handleAuthError(options: { force?: boolean } = {}) {
  const hasRefreshToken = localStorage.getItem(PERSISTENT_KEY_REFRESH_TOKEN);

  // Keep session when refresh may still work; force clears stale tokens after refresh failed
  if (hasRefreshToken && !options.force) {
    return;
  }

  // Store current page for redirect after login (only if we're not already on login page)
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath !== '/account/signin' && !currentPath.startsWith('/account/')) {
    localStorage.setItem('auth:redirectAfterLogin', currentPath);
  }

  // Clear all auth data
  localStorage.removeItem(PERSISTENT_KEY_AUTH_USER);
  localStorage.removeItem(PERSISTENT_KEY_ACCESS_TOKEN);
  localStorage.removeItem(PERSISTENT_KEY_REFRESH_TOKEN);

  // Dispatch custom event to notify auth context
  window.dispatchEvent(new CustomEvent('auth:logout'));

  // Redirect to login page
  if (window.location.pathname !== '/account/signin') {
    window.location.href = '/account/signin';
  }
}

SECURE_API.interceptors.request.use(requestInterceptor);
SECURE_API.interceptors.response.use(response => response, responseErrorInterceptor);
